import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../common/responsive';
import Select, { components } from 'react-select';
import styled, { useTheme } from 'styled-components';
import { ParagraphLargeText } from '../../../../components/Texts';
import { hapticsImpactLight } from '../../../../common/haptic';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { openModal } from '../../../../redux/slices/modals';
import ChevronRightIcon from '../../../../assets/icons/chevron-right.svg?react';

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const TokenSymbol = styled(ParagraphLargeText)`
  color: ${({ theme }) => theme.text.regular};
  font-size: 16px;

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const Option = ({ data, ...props }) => (
  <components.Option {...props}>
    <OptionContainer>
      <img
        src={data.logoURI || 'https://via.placeholder.com/20'}
        alt={data.symbol}
        style={{ width: '24px', height: '24px' }}
      />
      <TokenInfo>
        <TokenSymbol>{data.symbol}</TokenSymbol>
      </TokenInfo>
    </OptionContainer>
  </components.Option>
);

const SingleValueContainer = styled.div`
  padding-right: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SingleValue = ({ data, ...props }) => (
  <components.SingleValue {...props}>
    <SingleValueContainer>
      <img
        src={data.logoURI || 'https://via.placeholder.com/20'}
        style={{ width: '24px', height: '24px' }}
      />
      <TokenSymbol>{data.symbol}</TokenSymbol>
    </SingleValueContainer>
  </components.SingleValue>
);

const SelectedOptionContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.background.light};
  border-radius: 16px;
  cursor: pointer;
  padding-left: 15px;
  padding-right: 15px;
  height: 48px;
`;

const SelectedOptionSymbol = styled.img`
  width: 24px;
  height: 24px;
`;

const TokenSelector = ({ label, value, disabled, options = [], onChange }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const selectedOption =
    options.find((option) => option.symbol === value) || options[0];

  const handleChange = (option) => {
    onChange?.(option.symbol);
  };

  const theme = useTheme();
  const dispatch = useDispatch();

  if (isMobile) {
    return (
      <SelectedOptionContainer
        onClick={() => {
          hapticsImpactLight();
          dispatch(
            openModal({
              name: 'selectToken',
              props: {
                tokens: options,
                title: t('selectTokenModal.selectToken'),
                onContinue: (token) => {
                  dispatch(
                    openModal({
                      name: 'withdrawFunds',
                      props: { initialSelectedToken: token.symbol },
                    }),
                  );
                },
              },
            }),
          );
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SelectedOptionSymbol src={selectedOption?.logoURI} />
          <TokenSymbol>{selectedOption?.symbol}</TokenSymbol>
        </div>
        <ChevronRightIcon
          width={12}
          height={12}
          style={{ color: theme.text.title }}
        />
      </SelectedOptionContainer>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Select
        className='react-select'
        classNamePrefix='react-select'
        disabled={disabled}
        label={label}
        isSearchable={false}
        menuIsOpen={options.length > 1 ? undefined : false}
        styles={{
          option: (provided, state) => ({
            ...provided,
            backgroundColor: theme.background.primary,
            padding: '8px',
            margin: 0,
            color: state.isSelected
              ? `${theme.primary.accent}`
              : `${theme.text.regular}`,
          }),
          menuList: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
            borderBottom: `1px solid ${theme.border.secondary}`,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            borderLeft: `1px solid ${theme.border.secondary}`,
            borderRight: `1px solid ${theme.border.secondary}`,
            maxHeight: '200px',
          }),
          control: (provided) => ({
            ...provided,
            backgroundColor: theme.background.primary,
            border: `1px solid ${theme.border.secondary}`,
            borderRadius: 16,
            height: '46px',
          }),
          controlHovered: (provided) => ({
            ...provided,
            border: '1px solid red',
          }),
          menu: (provided) => ({
            ...provided,
            paddingTop: 10,
            height: 'fit-content',
            paddingBottom: 10,
            zIndex: 1000,
            margin: 0,
            padding: 0,
          }),
        }}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder='Select Token'
        components={{ Option, SingleValue }}
      />
    </div>
  );
};

export default TokenSelector;
